import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import {RcFile} from 'antd/lib/upload';

import {useLocale} from '../../provider/locale/locale-hook';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {ApiError} from '../../util/error';
import {PublicationListFilterType} from '../publication/publication-filter-type';

import {
    changePromotionStatus,
    createPromotion,
    deletePromotion,
    fetchPromotionById,
    fetchPromotions,
    fetchPromotionSelectedCompanies,
    updatePromotion,
    uploadPromotionImage,
} from './promotions-api';
import {
    CommonPromotionStatusEnum,
    PromotionDetailType,
    PromotionRequestType,
    PromotionSelectedCompaniesType,
    PromotionsListResponseType,
    UploadPromotionImageResponseType,
} from './promotions-type';

export const PROMOTIONS_QUERY_KEY = 'promotions';
const SINGLE_PROMOTION_QUERY_KEY = 'single-promotion';
const PROMOTION_SELECTED_COMPANIES_QUERY_KEY = 'promotion-selected-companies';

export function usePromotions(
    filter?: PublicationListFilterType,
    options?: {enabled?: boolean; staleTime?: number}
): UseInfiniteQueryResult<PromotionsListResponseType> {
    return useInfiniteQuery(
        [PROMOTIONS_QUERY_KEY, filter],
        async ({pageParam: page = 1}) => fetchPromotions(page, filter),
        {
            getPreviousPageParam: (firstPage) => (firstPage.previous && firstPage.page ? firstPage.page - 1 : 1),
            getNextPageParam: (lastPage) => (lastPage.next && lastPage.page ? lastPage.page + 1 : null),
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchInterval: 0,
            retry: 0,
            enabled: options?.enabled ?? true,
            // Keep the cached list across tab switches; only refetch when the filter (query key) changes.
            staleTime: options?.staleTime,
        }
    );
}

export function usePromotionSelectedCompanies(
    promotionId: number,
    enabled: boolean
): UseInfiniteQueryResult<PromotionSelectedCompaniesType> {
    return useInfiniteQuery(
        [PROMOTION_SELECTED_COMPANIES_QUERY_KEY, promotionId],
        async ({pageParam: page = 1}) => fetchPromotionSelectedCompanies(promotionId, page),
        {
            getPreviousPageParam: (firstPage) => (firstPage.previous && firstPage.page ? firstPage.page - 1 : 1),
            getNextPageParam: (lastPage) => (lastPage.next && lastPage.page ? lastPage.page + 1 : null),
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchInterval: 0,
            retry: 0,
            enabled,
        }
    );
}

export function usePromotion(promotionId: number | null, enabled: boolean): UseQueryResult<PromotionDetailType> {
    return useQuery([SINGLE_PROMOTION_QUERY_KEY, promotionId], () => fetchPromotionById(promotionId || 0), {
        enabled: enabled && Boolean(promotionId),
        retry: 0,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        cacheTime: 0,
    });
}

function getApiErrorMessage(error: ApiError): string | undefined {
    const errorData = error?.jsonData as {non_field_errors?: Array<string>};

    return errorData?.non_field_errors?.[0];
}

export function useCreatePromotion(): UseMutationResult<void, unknown, PromotionRequestType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const queryClient = useQueryClient();

    return useMutation([`create-${PROMOTIONS_QUERY_KEY}`], (data: PromotionRequestType) => createPromotion(data), {
        onSuccess: () => {
            snackbar.success(getLocalizedString('PROMOTIONS__SNACKBAR__CREATE_SUCCESS'));
            queryClient.invalidateQueries([PROMOTIONS_QUERY_KEY]);
        },
        onError: (error: ApiError) => {
            snackbar.error({
                message: getLocalizedString('PROMOTIONS__SNACKBAR__ERROR'),
                description: getApiErrorMessage(error) ?? getLocalizedString('ERROR__SOMETHING_WENT_WRONG_DETAILS'),
            });
        },
    });
}

export function useUpdatePromotion(promotionId: number): UseMutationResult<void, unknown, PromotionRequestType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const queryClient = useQueryClient();

    return useMutation(
        [`update-${PROMOTIONS_QUERY_KEY}`, promotionId],
        (data: PromotionRequestType) => updatePromotion(promotionId, data),
        {
            onSuccess: () => {
                snackbar.success(getLocalizedString('PROMOTIONS__SNACKBAR__UPDATE_SUCCESS'));
                queryClient.invalidateQueries([PROMOTIONS_QUERY_KEY]);
                queryClient.invalidateQueries([SINGLE_PROMOTION_QUERY_KEY, promotionId]);
                // The per-company catalog statuses change with the promotion, so the cached list must not be reused.
                queryClient.invalidateQueries([PROMOTION_SELECTED_COMPANIES_QUERY_KEY, promotionId]);
            },
            onError: (error: ApiError) => {
                snackbar.error({
                    message: getLocalizedString('PROMOTIONS__SNACKBAR__ERROR'),
                    description: getApiErrorMessage(error) ?? getLocalizedString('ERROR__SOMETHING_WENT_WRONG_DETAILS'),
                });
            },
        }
    );
}

export function useDeletePromotion(): UseMutationResult<void, unknown, number> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const queryClient = useQueryClient();

    return useMutation([`delete-${PROMOTIONS_QUERY_KEY}`], (promotionId: number) => deletePromotion(promotionId), {
        onSuccess: () => {
            snackbar.success(getLocalizedString('PROMOTIONS__SNACKBAR__DELETE_SUCCESS'));
            queryClient.invalidateQueries([PROMOTIONS_QUERY_KEY]);
        },
        onError: () => {
            snackbar.error({
                message: getLocalizedString('PROMOTIONS__SNACKBAR__ERROR'),
                description: getLocalizedString('ERROR__SOMETHING_WENT_WRONG_DETAILS'),
            });
        },
    });
}

export function useUploadPromotionImage(): UseMutationResult<UploadPromotionImageResponseType, unknown, RcFile> {
    return useMutation([`upload-${PROMOTIONS_QUERY_KEY}-image`], (file: RcFile) => uploadPromotionImage(file));
}

type ChangeStatusType = CommonPromotionStatusEnum.Paused | CommonPromotionStatusEnum.Sent;

export function useChangePromotionStatus(promotionId: number): UseMutationResult<void, unknown, ChangeStatusType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const queryClient = useQueryClient();

    return useMutation(
        [`status-${PROMOTIONS_QUERY_KEY}`, promotionId],
        (status: ChangeStatusType) => changePromotionStatus(promotionId, status),
        {
            onSuccess: () => {
                snackbar.success(getLocalizedString('PROMOTIONS__SNACKBAR__UPDATE_SUCCESS'));
                queryClient.invalidateQueries([PROMOTIONS_QUERY_KEY]);
                queryClient.invalidateQueries([SINGLE_PROMOTION_QUERY_KEY, promotionId]);
            },
            onError: (error: ApiError) => {
                snackbar.error({
                    message: getLocalizedString('PROMOTIONS__SNACKBAR__ERROR'),
                    description: getApiErrorMessage(error) ?? getLocalizedString('ERROR__SOMETHING_WENT_WRONG_DETAILS'),
                });
            },
        }
    );
}
